import {
  type QualityGradingModelRow,
  DistributionRowWeightClassEnum,
  DistributionRowQualityClassEnum,
  HarvestExplanationsEnum,
  HarvestDemandPutSpeciesEnum,
  HarvestDemandPutCertificatesEnum,
  HarvestPlantCostTypeEnum,
} from 'client';
import {MetricsEnum} from 'components/OverviewTable/interfaces';
import {type IQualityGrading, KPIColumnEnum, WeightUnits, type PlanKPIs, type FormSelectionEnum} from 'interfaces';

export const RIGHT_DRAWER_ZINDEX_DIFF = 2;
export const LEFT_DRAWER_WIDTH = 210;
export const DEFAULT_POLLING_INTERVAL = 1000;
export const DEFAULT_OVERVIEW_COLUMNS = 50;
export const DEFAULT_SHORT_TERM_COLUMNS = 50;
export const OVERVIEW_TABLE_PERIODS = 50;
export const DELOUSING_MORTALITY_RATIO = 0.1;
export const SWR_OPTIONS = {
  revalidateOnFocus: false,
};
export const SWR_IMMUTABLE_OPTIONS = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

export const MAX_FISH_WEIGHT_GRAMS = 15000;
export const MAX_HARVEST_BIOMASS_TONS = 10000;
export const MIN_HARVEST_BIOMASS_TONS = 5;
export const DEFAULT_MAP_CENTER = {
  lat: 60.38,
  lng: 5.33,
};

export const APP_TOP_BAR_HEIGHT = 49;

export const TOTAL_CHART_HEIGHT = 190;
export const CHART_MIN_WIDTH = 1200;

export const defaultKPIS: PlanKPIs = [
  KPIColumnEnum.harvestVolumeHog,
  KPIColumnEnum.harvestVolumeLwe,
  KPIColumnEnum.averageHarvestWeight,
  KPIColumnEnum.mabUtilization,
  KPIColumnEnum.turnover,
];

export const harvestVolumeChartKPIs: PlanKPIs = [
  KPIColumnEnum.harvestVolumeHog,
  KPIColumnEnum.harvestVolumeLwe,
  KPIColumnEnum.harvestCount,
  KPIColumnEnum.averageHarvestWeight,
];

export const SUPPORTED_FISH_SPECIES = [HarvestDemandPutSpeciesEnum.Salmon, HarvestDemandPutSpeciesEnum.Trout];

export const SUPPORTED_FISH_CERTIFICATES = [
  HarvestDemandPutCertificatesEnum.Asc,
  HarvestDemandPutCertificatesEnum.Fos,
  HarvestDemandPutCertificatesEnum.Gladlaks,
  HarvestDemandPutCertificatesEnum.GlobalGap,
  HarvestDemandPutCertificatesEnum.Naturland,
  HarvestDemandPutCertificatesEnum.Organic,
  HarvestDemandPutCertificatesEnum.PracticeGaabap,
  HarvestDemandPutCertificatesEnum.SoilAssociation,
  HarvestDemandPutCertificatesEnum.Stp,
  HarvestDemandPutCertificatesEnum.Debio,
];

export const SUPPORTED_WEIGHT_CLASSES = [
  DistributionRowWeightClassEnum._01,
  DistributionRowWeightClassEnum._12,
  DistributionRowWeightClassEnum._23,
  DistributionRowWeightClassEnum._34,
  DistributionRowWeightClassEnum._45,
  DistributionRowWeightClassEnum._56,
  DistributionRowWeightClassEnum._67,
  DistributionRowWeightClassEnum._78,
  DistributionRowWeightClassEnum._89,
  DistributionRowWeightClassEnum._9,
];

export const SUPPORTED_QUALITY_CLASSES = [
  DistributionRowQualityClassEnum.Superior,
  DistributionRowQualityClassEnum.Ordinary,
  DistributionRowQualityClassEnum.Production,
  DistributionRowQualityClassEnum.Vrak,
];

export const DEFAULT_FIXED_QUALITY_GRADING: IQualityGrading = {
  superior: 0.92,
  ordinary: 0.04,
  production: 0.03,
  vrak: 0.01,
};

export const getSTVEnabledActionTypes = () =>
  ['input', 'harvest', 'delousing', 'liveFishTransaction'] satisfies Array<keyof typeof FormSelectionEnum>;

const HarvestReasons = Object.values(HarvestExplanationsEnum); // BE Reasons

const NORWAY_HARVEST_REASONS = [
  HarvestExplanationsEnum.ConcessionMab,
  HarvestExplanationsEnum.HarvestPlantCapacity,
  HarvestExplanationsEnum.SiteMab,
  HarvestExplanationsEnum.CageMab,
  HarvestExplanationsEnum.Fallowing,
  HarvestExplanationsEnum.FishValue,
  HarvestExplanationsEnum.HarvestDemand,
  HarvestExplanationsEnum.Undefined,
];

const CHILE_HARVEST_REASONS = [
  HarvestExplanationsEnum.HarvestPlantCapacity,
  HarvestExplanationsEnum.SiteConcessionLimit,
  HarvestExplanationsEnum.CageMab,
  HarvestExplanationsEnum.Fallowing,
  HarvestExplanationsEnum.FishValue,
  HarvestExplanationsEnum.HarvestDemand,
  HarvestExplanationsEnum.Undefined,
];

export const SUPPORTED_HARVEST_REASONS: (isChilePlan: boolean) => HarvestExplanationsEnum[] = (isChilePlan: boolean) =>
  isChilePlan ? CHILE_HARVEST_REASONS : NORWAY_HARVEST_REASONS;

export const DEFAULT_STARTUP_COST = 20000000;
export const DEFAULT_OPERATIONAL_COST = 20000;
export const DEFAULT_HARVEST_COST = 20000;

export const DEFAULT_CAGE_MAX_DENSITY = 25;
export const DEFAULT_CAGE_MAX_VOLUME = 37000;
export const DEFAULT_CAGE_MAX_COUNT = 200000;
export const DEFAULT_SITE_MAB = 10000000;

export const STV_ENABLED_ACTION_TYPES = ['input', 'harvest', 'delousing', 'liveFishTransaction'] satisfies Array<
  keyof typeof FormSelectionEnum
>;

export const MIN_INPUT_COUNT = 3000;
export const MIN_INPUT_WEIGHT = 1;
export const MAX_INPUT_WEIGHT = 10000;

export const QUALITY_CLASSES: Array<keyof QualityGradingModelRow> = ['superior', 'ordinary', 'production', 'vrak'];

export const ICON_SIZE = 'small';

export const DEFAULT_WEIGHT_UNIT = WeightUnits.lwe;
export const DEFAULT_FIXED_MORTALITY_RATE = 0.3;
export const DEFAULT_ACCUMULATED_MORTALITY_RATE = 14;

export const DEFAULT_OVERVIEW_TABLE_METRIC = MetricsEnum.biomass;
export const DEFAULT_HARVEST_PLANT_COST_TYPE = HarvestPlantCostTypeEnum.Biomass;

export const EVENTS_TABLE_FILTERS_STORE_KEY = 'events-table-filters';
export const FISHGROUP_VIEW_FILTERS_STORE_KEY = 'fish-group-view-filters';
export const SMOLT_SUPPLY_VIEW_FILTERS_STORE_KEY = 'smolt-supply-view-filters';
export const HARVEST_RECOMMENDATIONS_FILTERS_STORE_KEY = 'harvest-recommendations-filters';
export const TABLE_VIEW_FILTERS_STORE_KEY = 'table-view-filters';
export const SHORT_TERM_VIEW_TAGS_STORE_KEY = 'short-term-view-tags';
export const PLANS_FILTER_STORE_KEY = 'plans-filter-tags';

export const QUALITY_CLASS_COLOR = {
  [DistributionRowQualityClassEnum.Superior]: 'good',
  [DistributionRowQualityClassEnum.Ordinary]: 'info',
  [DistributionRowQualityClassEnum.Production]: 'warning',
  [DistributionRowQualityClassEnum.Vrak]: 'error',
};
